import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, CircleAlert } from "lucide-react";
import { Page, Spinner } from "../../components";
import { AuthLayout } from "./AuthLayout";
import { client, unwrap } from "../../api/client";
import { ApiError } from "../../api/errors";
import { t } from "../../i18n/t";

type Status = "pending" | "success" | "invalid" | "failed";

/**
 * P1a §7: `POST /api/v1/auth/verify-email`, reached from the link a registration email carries. It POSTs on
 * mount — there is no form here, the token in the URL is the whole input — and `invalid_token` covers "no such
 * token", "expired" and "already used" alike (§7's anti-enumeration rule), so this screen never tries to tell
 * those apart either.
 */
export function VerifyEmailScreen(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [status, setStatus] = useState<Status>("pending");
  const requested = useRef(false);

  useEffect(() => {
    if (token === "" || requested.current) return;
    requested.current = true;
    void (async () => {
      try {
        await unwrap(client.POST("/api/v1/auth/verify-email", { body: { token } }));
        setStatus("success");
      } catch (caught) {
        // `invalid_token` is the server's verdict on the link. Anything else (403, 5xx, network) is a failed
        // request, and telling the user their link is invalid or expired would be wrong.
        setStatus(caught instanceof ApiError && caught.is("invalid_token") ? "invalid" : "failed");
      }
    })();
  }, [token]);

  if (token === "" || status === "invalid") {
    // The expired-link card (AuthFlows card 06's treatment, with this flow's own sentence).
    return (
      <AuthLayout>
        <Page narrow title={t("auth.link_expired_title")}>
          <div className="card card--auth card--auth--center">
            <span className="authicon authicon--fail" aria-hidden="true">
              <CircleAlert size={24} strokeWidth={2} />
            </span>
            <p className="authcenter">
              {token === "" ? t("auth.verify.missing_token") : t("auth.verify.invalid")}
            </p>
            <Link className="button button--auth button--primary" to="/auth/login">
              {t("auth.verify.go_to_login")}
            </Link>
          </div>
        </Page>
      </AuthLayout>
    );
  }

  if (status === "failed") {
    return (
      <AuthLayout>
        <Page narrow title={t("auth.verify.title")}>
          <p role="alert">{t("auth.verify.failed")}</p>
        </Page>
      </AuthLayout>
    );
  }

  if (status === "pending") {
    return (
      <AuthLayout>
        <Page narrow title={t("auth.verify.title")}>
          <Spinner />
          <p>{t("auth.verify.pending")}</p>
        </Page>
      </AuthLayout>
    );
  }

  // AuthFlows card 05: the centred verified card with the check tile.
  return (
    <AuthLayout>
      <Page narrow title={t("auth.verify.success_title")}>
        <div className="card card--auth card--auth--center">
          <span className="authicon authicon--pass" aria-hidden="true">
            <Check size={24} strokeWidth={2} />
          </span>
          <p className="authcenter">{t("auth.verify.success")}</p>
          <Link className="button button--auth button--primary" to="/auth/login">
            {t("auth.verify.go_to_login")}
          </Link>
        </div>
      </Page>
    </AuthLayout>
  );
}
